import {
  alcoveCornerLineFrame,
  alcoveWallLineFrame,
  cornerChamferMax,
  CornerName,
  EdgeName,
  edgeSegments,
  expandPlanBounds,
  FloorPlanCorner,
  FloorPlanEdgeSegment,
  FloorPlanRoom,
  geometryBounds,
  LineFrame,
  OuterWhich,
  Point,
  roomCorner,
  roomCornerLineFrame,
  SQUARE_CORNER,
  WALL_THICKNESS,
  worldOnLine,
} from '@kaiizen/kai-floor-plan';
import { wallElementRings, wallOuterRing } from '../room-geometry';
import { lineGaps } from './edgeRefs';
import { CORNER_POINT, RECT_CORNER } from './editorConstants';
import type { ChamferData, EditorMutableState, Rect, Selection } from './editorState';
import { edgeWorld, isHorizontal, rectCornerPoint } from './geometryLib';
import type { InputKind } from './inputKind';
import { ALL_PLAN_LAYERS, type PlanLayers, showsElement } from './planLayers';
import type { Viewport } from './viewport';

export { geometryBounds, outwardReach } from '@kaiizen/kai-floor-plan';

/** The shape to render: the in-flight draft during a drag, else the committed shape. */
export function displayShape(st: EditorMutableState): FloorPlanRoom {
  return st.draftShape ?? st.shape;
}

/**
 * The floor slab every plan paints under the typed floors and the wall band:
 * the room's floor out to the outer wall line, as in 3D, so an opening shows
 * floor through the wall's thickness. The void of each hollow wall element
 * the layers show is cut out, so the background shows through it. One
 * polygon for an even-odd fill; null when the walls enclose nothing.
 */
export function floorSlabRings(shape: FloorPlanRoom, layers: PlanLayers = ALL_PLAN_LAYERS): Point[][] | null {
  const outerRing = wallOuterRing(shape);
  if (outerRing.length < 3) return null;
  const voids = (shape.freeElements ?? [])
    .filter(element => element.customAreaType === 'wall' && showsElement(layers, element))
    .map(element => wallElementRings(element).inner)
    .filter((ring): ring is Point[] => !!ring);
  return [outerRing, ...voids];
}

export function fitViewport(shape: FloorPlanRoom, stageW: number, stageH: number, vp: Viewport): Viewport {
  // The plan draws the wall band one thickness outside the measured geometry.
  const bounds = expandPlanBounds(geometryBounds(shape), WALL_THICKNESS);
  const pts: Point[] = [
    { x: bounds.minX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.maxY },
    { x: bounds.minX, y: bounds.maxY },
  ];
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const bw = Math.max(1, maxX - minX), bh = Math.max(1, maxY - minY);
  const cw = stageW || 800, ch = stageH || 600;
  const pad = 0.78;
  const scale = Math.max(2, Math.min((cw * pad) / bw, (ch * pad) / bh));
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  return { scale, offset: { x: cw / 2 - cx * scale, y: ch / 2 - cy * scale } };
}

/** Bare (segment-free) middle of the first gap along a line, for add-affordances. */
export function lineGapPoint(segs: FloorPlanEdgeSegment[], lf: LineFrame): Point | null {
  const gap = lineGaps(segs, 0, lf.length)[0];
  return gap ? worldOnLine(lf, (gap.gs + gap.ge) / 2, 0) : null;
}

export function addIconPoint(st: EditorMutableState, shape: FloorPlanRoom): Point | null {
  const sel = st.selection;
  if (sel.kind === 'edge' && st.edgeGap) {
    const { edge, gs, ge } = st.edgeGap;
    return edgeWorld(shape, edge, (gs + ge) / 2, 0);
  }
  if (sel.kind === 'alcoveWall') {
    const lf = alcoveWallLineFrame(shape, sel.edge, sel.index, sel.wall);
    if (!lf) return null;
    const wallSegs = edgeSegments(shape, sel.edge)[sel.index]?.[sel.wall]?.segments ?? [];
    if (st.wallGap) return worldOnLine(lf, (st.wallGap.gs + st.wallGap.ge) / 2, 0);
    return lineGapPoint(wallSegs, lf);
  }
  if (sel.kind === 'corner') {
    const lf = roomCornerLineFrame(shape, sel.corner);
    if (!lf || lf.length < 0.1) return null;
    return lineGapPoint(shape[sel.corner]?.segments ?? [], lf);
  }
  if (sel.kind === 'alcoveCorner') {
    const lf = alcoveCornerLineFrame(shape, sel.edge, sel.index, sel.which);
    if (!lf || lf.length < 0.1) return null;
    return lineGapPoint(edgeSegments(shape, sel.edge)[sel.index]?.[sel.which]?.segments ?? [], lf);
  }
  return null;
}

export function chamferData(st: EditorMutableState, sel: Selection): ChamferData | null {
  const shape = displayShape(st);
  if (sel.kind === 'corner') {
    const name = sel.corner;
    const corner = CORNER_POINT[name](shape);
    const def = RECT_CORNER[name];
    const max = cornerChamferMax(shape, name);
    const c = roomCorner(shape, name);
    return {
      ch: { dx: c.dx, dy: c.dy },
      corner,
      sx: def.sx,
      sy: def.sy,
      frame: { x: 0, y: 0, w: shape.width, h: shape.length },
      maxDx: max.dx,
      maxDy: max.dy,
    };
  }
  if (sel.kind === 'alcoveCorner') {
    const al = edgeSegments(shape, sel.edge)[sel.index];
    if (!al) return null;
    const which = sel.which;
    const alDepth = al.depth ?? 0;
    const corner = which === 'backLeftCorner'
      ? edgeWorld(shape, sel.edge, al.position, alDepth)
      : edgeWorld(shape, sel.edge, al.position + al.width, alDepth);
    const box = [
      edgeWorld(shape, sel.edge, al.position, 0),
      edgeWorld(shape, sel.edge, al.position, alDepth),
      edgeWorld(shape, sel.edge, al.position + al.width, alDepth),
      edgeWorld(shape, sel.edge, al.position + al.width, 0),
    ];
    const minX = Math.min(...box.map(p => p.x)), maxX = Math.max(...box.map(p => p.x));
    const minY = Math.min(...box.map(p => p.y)), maxY = Math.max(...box.map(p => p.y));
    const sx = Math.abs(corner.x - minX) < 1e-6 ? 1 : -1;
    const sy = Math.abs(corner.y - minY) < 1e-6 ? 1 : -1;
    const horiz = isHorizontal(sel.edge);
    const blc = al.backLeftCorner ?? { dx: 0, dy: 0 };
    const brc = al.backRightCorner ?? { dx: 0, dy: 0 };
    const chCorner = which === 'backLeftCorner' ? blc : brc;
    let maxDx: number, maxDy: number;
    if (horiz) {
      const otherAlong = which === 'backLeftCorner' ? brc.dx : blc.dx;
      maxDx = Math.max(0, al.width - otherAlong);
      maxDy = Math.max(0, alDepth);
    } else {
      const otherAlong = which === 'backLeftCorner' ? brc.dy : blc.dy;
      maxDy = Math.max(0, al.width - otherAlong);
      maxDx = Math.max(0, alDepth);
    }
    return {
      ch: { dx: chCorner.dx, dy: chCorner.dy },
      corner,
      sx,
      sy,
      frame: { x: minX, y: minY, w: maxX - minX, h: maxY - minY } as Rect,
      maxDx,
      maxDy,
    };
  }
  if (sel.kind === 'innerCorner') {
    const r = shape.freeElements?.[sel.index];
    if (!r) return null;
    const which = sel.which;
    const def = RECT_CORNER[which];
    return {
      ch: r[which] ?? SQUARE_CORNER,
      corner: rectCornerPoint(r, which),
      sx: def.sx,
      sy: def.sy,
      frame: { x: r.x, y: r.y, w: r.width, h: r.length },
      maxDx: Math.max(0, r.width - (r[def.hN]?.dx ?? 0)),
      maxDy: Math.max(0, r.length - (r[def.vN]?.dy ?? 0)),
    };
  }
  return null;
}

/** Whether a corner is cut: either leg longer than a millionth of a foot. */
export const hasChamfer = (corner: FloorPlanCorner): boolean => corner.dx > 1e-6 || corner.dy > 1e-6;

/**
 * Where a chamfer's drag handle sits: on the square corner pointing inward,
 * or once cut, at the cut's far leg pointing back out.
 */
export const cornerHandlePlacement = (chamfer: ChamferData): { pt: Point; sx: number; sy: number } => {
  const chamfered = hasChamfer(chamfer.ch);
  return {
    pt: { x: chamfer.corner.x + chamfer.sx * chamfer.ch.dx, y: chamfer.corner.y + chamfer.sy * chamfer.ch.dy },
    sx: chamfered ? -chamfer.sx : chamfer.sx,
    sy: chamfered ? -chamfer.sy : chamfer.sy,
  };
};

/** A chamfer drag triangle's legs, and how far it sits back from its anchor along each axis (px). */
const CORNER_TRIANGLE_LEG = 14, CORNER_TRIANGLE_GAP = 8;

/**
 * A chamfer's drag triangle on screen, from its anchor (`cornerHandlePlacement`
 * projected) and its pointing signs: a right triangle set back 8 px from the
 * anchor, its 14 px legs measured from that corner, turned `angle` degrees
 * about the anchor so it hugs a rotated host's corner. Its centroid is the
 * handle's visual center.
 */
export const cornerHandleTriangle = (anchor: Point, sx: number, sy: number, angle = 0): [Point, Point, Point] => {
  const radians = (angle * Math.PI) / 180;
  const cos = Math.cos(radians), sin = Math.sin(radians);
  const offset = (from: Point, x: number, y: number): Point => ({ x: from.x + (x * cos - y * sin), y: from.y + (x * sin + y * cos) });
  const corner = offset(anchor, -sx * CORNER_TRIANGLE_GAP, -sy * CORNER_TRIANGLE_GAP);
  return [corner, offset(corner, sx * CORNER_TRIANGLE_LEG, 0), offset(corner, 0, sy * CORNER_TRIANGLE_LEG)];
};

/** The two ends of a chamfer's diagonal, one on each leg, through `toScreen`. */
export const chamferEndpoints = (chamfer: ChamferData, toScreen: (point: Point) => Point): [Point, Point] => [
  toScreen({ x: chamfer.corner.x + chamfer.sx * chamfer.ch.dx, y: chamfer.corner.y }),
  toScreen({ x: chamfer.corner.x, y: chamfer.corner.y + chamfer.sy * chamfer.ch.dy }),
];

/**
 * How far (px) a selected door's, window's or opening's trash icon centers
 * inward from its host line: 26, or 58 under touch, a touch target clear of
 * its slide strip.
 */
export const segmentTrashOffset = (inputKind: InputKind = 'pointer'): number => (inputKind === 'touch' ? 58 : 26);

/**
 * How far a door's, window's or opening's handles reach from its host line,
 * in feet at `scale` px per foot. Its slide strip and resize edges reach
 * outward across the whole wall band and 8 px past it, and inward 24 px into
 * the room, 36 px under touch so the strip stays a whole touch target. While
 * it is selected its trash icon centers `trash` inward (`segmentTrashOffset`).
 * The plans draw the handles and hit-test them to these reaches.
 */
export const segmentHandleReach = (scale: number, inputKind: InputKind = 'pointer'): { inward: number; outward: number; trash: number } => ({
  inward: (inputKind === 'touch' ? 36 : 24) / scale,
  outward: WALL_THICKNESS + 8 / scale,
  trash: segmentTrashOffset(inputKind) / scale,
});

/** Map a chamfer data-handle payload (room/alcove/inner) to its Selection. */
export function parseChamferSelection(rest: string[]): Selection | null {
  if (rest[0] === 'room') return { kind: 'corner', corner: rest[1] as CornerName };
  if (rest[0] === 'alcove') return { kind: 'alcoveCorner', edge: rest[1] as EdgeName, index: Number(rest[2]), which: rest[3] as OuterWhich };
  if (rest[0] === 'inner') return { kind: 'innerCorner', index: Number(rest[1]), which: rest[2] as CornerName };
  return null;
}
